import { of } from 'rxjs'
import { map as rxMap, debounceTime, switchMap, tap } from 'rxjs/operators'
import { requestSectionContent } from './QAService'
import { accordionGroup, accordionContent } from 'helpers/accordion'
import { mapResponse } from 'libraries/front-end-core/helpers/dataflow'
import { pick, map, zip, uniq, filter, intersection, values } from 'libraries/lodash'
import debug from 'libraries/front-end-core/core/debug'
import { RESPONSE_PENDING } from 'libraries/front-end-core/constants/responses'

/**
* QA Question to AccordionContent map
* @return {Object}
*/
const toAccordionContent = function (flavour, path, item) {
  const { tocId, question: title, answer: content, questionNumber } = item
  const itemPath = [...path, tocId]

  const stateSource = of({ expanded: true })

  return accordionContent({
    id: itemPath.join('/'),
    title,
    prefix: `Q ${questionNumber}:`,
    path: itemPath,
    content,
    hasAccess: true,
    stateSource
  })
}

/**
* QA Section to AccordionGroup map
* @return {Object}
*/
const toAccordionGroup = function (flavour, nodeId, resp, paths) {
  const node = resp.nodes[nodeId]
  let { path, title, childSections } = node
  const [nextGroup = [], ...tail] = paths
  childSections = intersection(childSections, nextGroup) // intersection uses the order of the first array, which is the one we want to keep

  const group = childSections.length ? toSectionGroup(flavour, resp, [childSections, ...tail]) : []

  // Can't see that the filter does anything but there might be a default interator we rely on?
  const mappedWithTocs = filter(map(resp.sections[nodeId], tocId => resp.questions[tocId]))
  const questions = map(mappedWithTocs, toAccordionContent.bind(null, flavour, path))

  return accordionGroup({
    id: path.join('/'),
    title,
    path,
    groupSource: of([...questions, ...group]),
    stateSource: of({ expanded: true }),
    hasAccess: true
  })
}

const requestFilteredSectionContent = function (flavour, sectionId, selectedIds) {
  return requestSectionContent(flavour, sectionId).pipe(
    rxMap(mapResponse(resp => ({ ...resp, questions: pick(resp.questions, selectedIds) }))),
    debounceTime(5)
  )
}

const toSectionGroup = function (flavour, resp, paths) {
  if (paths.length === 0) {
    return []
  }
  const [current, ...tail] = paths
  return current.map(id => toAccordionGroup(flavour, id, resp, tail))
}

/**
* Observable to feed Accordion at any level with list of Questions and Sections
* @return {Observable}
*/
export const requestRoot = function ({ flavour, sectionId, selectedIds }) {
  const Response = requestFilteredSectionContent(flavour, sectionId, selectedIds).pipe(
    rxMap(mapResponse(
      resp => {
        const parentPaths = values(
          pick(resp.paths, map(resp.questions, 'sectionId'))
        )

        const paths = zip(...parentPaths).map(a => filter(uniq(a)))
        return toSectionGroup(flavour, resp, paths)
      }
    ))
  )

  return of(RESPONSE_PENDING).pipe(
    tap(() => debug.time(`Request group ${flavour} ${sectionId}`)),
    switchMap(() => Response),
    tap(() => debug.timeEnd(`Request group ${flavour} ${sectionId}`))
  )
}
